<template>
  <BModal
    modal-name="bulkAssignOperator"
    width="480px"
    height="410px"
    :delegate="modalDelegate"
  >
    <BHeightAdjuster>
      <BModalHeader
        center
        class="modal-header"
        :title="$t('operatorAssign.bulkUpdateTitle')"
        @modal-close="handleClose"
      />
      <BModalBody class="modal-body">
        <BulkUpdateModalTarget
          :label="$t('operatorAssign.bulkTarget')"
          :count="selectedCallTargetIds.length"
        />
        <BListItem>
          <template #header>
            <div>{{ $t('operatorAssign.assignedOperator') }}</div>
          </template>
          <BSelect
            v-model="selectedOperatorId"
            class="operator-select"
            :items="filteredActiveUsers"
            item-text="name"
            item-value="id"
            :placeholder="$t('operatorAssign.select')"
            filterable
          />
        </BListItem>
        <BulkUpdateOverLimitMessage
          :visible="isOverLimit"
          :limit="BULK_UPDATE_LIMIT"
        />
      </BModalBody>
      <BulkUpdateModalFooter
        :loading="isLoading"
        :disabled="isUpdateDisabled"
        @cancel="handleClose"
        @submit="submitAssignOperator"
      />
    </BHeightAdjuster>
  </BModal>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import BulkUpdateModalFooter from '@/components/organisms/user/leadList/BulkUpdateModalFooter.vue';
import BulkUpdateModalTarget from '@/components/organisms/user/leadList/BulkUpdateModalTarget.vue';
import BulkUpdateOverLimitMessage from '@/components/organisms/user/leadList/BulkUpdateOverLimitMessage.vue';
import { useUsers } from '@/composable/user/user/users';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_MODAL, KEY_OF_TOAST } from '@/injection-keys';
import { key } from '@/store/index';

type TProps = {
  selectedCallTargetIds: number[];
};

const props = defineProps<TProps>();

const MODAL_NAME = 'bulkAssignOperator';
const BULK_UPDATE_LIMIT = 100;
const i18n = useI18n();
const store = useStore(key);
const modal = inject(KEY_OF_MODAL);
const toast = inject(KEY_OF_TOAST);
const { wait, doActionWithWait } = useWait();
const { activeUsers } = useUsers();

const selectedOperatorId = ref<number | null>(null);

const filteredActiveUsers = computed(() => {
  const unassignedOption = [
    {
      name: i18n.t('operatorAssign.unassigned'),
      id: 0,
    },
  ];
  return unassignedOption.concat(activeUsers.value);
});

const isOverLimit = computed<boolean>(() => props.selectedCallTargetIds.length > BULK_UPDATE_LIMIT);
const isUpdateDisabled = computed<boolean>(() => selectedOperatorId.value == null || isOverLimit.value);
const isLoading = computed<boolean>(() => wait.is(['assignOperatorWait', 'withdrawOperatorsWait']));

const modalDelegate = {
  beforeClose: () => {
    selectedOperatorId.value = null;
  },
};

const handleClose = () => {
  modal.hide(MODAL_NAME);
};

const submitAssignOperator = async () => {
  if (selectedOperatorId.value !== 0) {
    await assignOperator();
  } else {
    await withdrawOperators();
  }
  handleClose();
};

const showAssignResultToast = (succeeded: boolean) => {
  const target = i18n.t('operatorAssign.assignedOperator');
  const action = i18n.t('general.update.text');
  if (succeeded) {
    toast.show(i18n.t('general.done.withTarget', { target, action }));
  } else {
    toast.show({ message: i18n.t('general.fail.withTarget', { target, action }), type: 'error' });
  }
};

const assignOperator = async () => {
  await doActionWithWait('assignOperatorWait', async () => {
    try {
      await store.dispatch('user/assignOperatorAction', {
        body: {
          operatorId: selectedOperatorId.value,
          callTargetIds: props.selectedCallTargetIds,
        },
        errorHandlers: { disableDefaultErrorHandler: true },
      });
      showAssignResultToast(true);
    } catch {
      showAssignResultToast(false);
    }
  });
  await getCallList();
};

const withdrawOperators = async () => {
  await doActionWithWait('withdrawOperatorsWait', async () => {
    try {
      await store.dispatch('user/withdrawOperatorsAction', {
        body: { callTargetIds: props.selectedCallTargetIds },
        errorHandlers: { disableDefaultErrorHandler: true },
      });
      showAssignResultToast(true);
    } catch {
      showAssignResultToast(false);
    }
  });
  await getCallList();
};

const getCallList = async () => {
  // UX改善のため、データ再読込時に全体ローディングしないようになっている
  toast.show({ message: i18n.t('callList.update.loading'), type: 'info' });
  await doActionWithWait('getCallTargetsBackgroundWait', async () => {
    await store.dispatch('user/getCallTargetsAction');
  });
  toast.show({ message: i18n.t('callList.update.done'), type: 'success' });
};
</script>

<style lang="scss" scoped>
  .modal-body {
    padding: $basespace-600;
  }
</style>
